import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MousePointer2 } from "lucide-react";
import { Link } from "react-router-dom";
import { LineReveal } from "@/components/shared/Reveal";
import { CircuitBg } from "@/components/shared/CircuitBg";
import { DottedSurfaceHero } from "@/components/shared/DottedSurfaceHero";
import { LightningHero } from "@/components/shared/LightningHero";

const HERO_IMG =
  "https://images.unsplash.com/photo-1698621177801-dc163e22256c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

export function Hero({ settings }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.85, 1]);

  const eyebrow = settings?.hero_eyebrow || "ENDÜSTRİYEL ELEKTRİK & OTOMASYON";
  const titleLines = (settings?.hero_title || "Endüstriyel gücü\nmühendislikle\nbuluşturuyoruz.").split("\n");
  const subtitle = settings?.hero_subtitle || "";

  return (
    <section ref={ref} data-testid="hero" className="relative flex items-center bg-navy overflow-hidden min-h-[100svh] py-28 md:py-0 md:h-[92vh] md:min-h-[680px]">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img src={HERO_IMG} alt="Endüstriyel kontrol sistemleri" className="w-full h-full object-cover" fetchPriority="high" />
      </motion.div>

      <motion.div className="absolute inset-0 bg-gradient-to-b from-navy/50 via-navy/40 to-navy" style={{ opacity: overlayOpacity }} />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/75 to-navy/20" />
      <div className="absolute inset-0 grid-lines-dark opacity-50" />
      <DottedSurfaceHero className="z-[1]" />
      <LightningHero
        className="z-[2] right-[-8%] top-0 h-full w-[68%] md:w-[62%]"
        hue={190}
        xOffset={-0.08}
        speed={0.42}
        intensity={0.2}
        size={1.55}
      />
      <CircuitBg className="absolute z-[3] right-0 top-1/4 w-[55%] h-1/2 hidden md:block" opacity={0.34} />

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-8">
        <motion.div
          className="flex items-center gap-3 mb-7"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
        >
          <span className="h-px w-10 bg-cyan" />
          <span className="text-cyan text-xs md:text-sm font-semibold tracking-[0.25em] uppercase">{eyebrow}</span>
        </motion.div>

        <h1 className="font-head font-extrabold text-white tracking-tight text-[10vw] leading-[1.14] sm:text-6xl lg:text-7xl xl:text-[5rem]">
          <LineReveal lines={titleLines} delay={0.25} lineClassName="pb-[0.12em]" />
        </h1>

        <motion.p
          className="mt-8 max-w-xl text-base md:text-lg text-silver/85 leading-relaxed"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
        >
          {subtitle}
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
        >
          <Link
            to="/hizmetler"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center justify-center gap-2 bg-cyan hover:bg-tech text-navy hover:text-white font-semibold text-sm px-7 py-4 transition-colors duration-300 clip-tag"
          >
            HİZMETLERİMİZİ KEŞFEDİN
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/iletisim"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-white hover:border-cyan hover:text-cyan font-semibold text-sm px-7 py-4 transition-colors duration-300"
          >
            PROJENİZİ GÖRÜŞELİM
          </Link>
        </motion.div>
      </div>

      {/* bottom technical label + scroll indicator */}
      <div className="absolute z-10 bottom-6 left-0 right-0">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 flex items-center justify-between">
          <motion.div
            className="hidden md:flex items-center gap-4 text-[11px] tracking-[0.28em] text-silver/50 uppercase"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
          >
            <span>Electrical Engineering</span><span className="text-cyan">/</span>
            <span>Automation</span><span className="text-cyan">/</span>
            <span>Industrial Solutions</span>
          </motion.div>
          <motion.div
            className="flex items-center gap-2 text-silver/60"
            initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 1] }}
            transition={{ delay: 1.2, duration: 2, repeat: Infinity, repeatType: "reverse" }}
          >
            <MousePointer2 className="w-4 h-4" />
            <span className="text-[11px] tracking-widest uppercase">Kaydırın</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
